<template>
  <svg
    class="inline-block align-middle flex-shrink-0"
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
    :role="title ? 'img' : undefined"
    :aria-hidden="title ? undefined : 'true'"
    :aria-labelledby="title ? titleId : undefined"
  >
    <title v-if="title" :id="titleId">{{ title }}</title>
    <path :d="path" />
  </svg>
</template>

<script lang="ts">
export const IconName = {
  Shield: 'shield',
  ChevronUp: 'chevron-up',
  ChevronDown: 'chevron-down',
  X: 'x',
  Calendar: 'calendar',
  LayoutDetail: 'layout-detail',
  LayoutCompact: 'layout-compact',
  Sort: 'sort',
  Download: 'download',
  RotateCcw: 'rotate-ccw',
  Plus: 'plus',
  Copy: 'copy',
  Trash: 'trash',
  Save: 'save',
  Play: 'play',
  Pencil: 'pencil',
  MoreVertical: 'more-vertical',
  Filter: 'filter',
  Check: 'check',
  Move: 'move',
  Crown: 'crown',
} as const

export type IconName = (typeof IconName)[keyof typeof IconName]
</script>

<script setup lang="ts">
import { computed, useId } from 'vue'

export interface AppIconProps {
  readonly name: IconName
  readonly size?: number
  readonly strokeWidth?: number
  readonly title?: string
}

const ICON_PATHS: Readonly<Record<IconName, string>> = {
  shield: 'M12 3 4 6v6c0 4.5 3.5 8 8 9 4.5-1 8-4.5 8-9V6l-8-3Z',
  'chevron-up': 'M6 14l6-6 6 6',
  'chevron-down': 'M6 10l6 6 6-6',
  x: 'M6 6l12 12M18 6 6 18',
  calendar:
    'M8 3v3M16 3v3M4 8h16M5 6h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1Z',
  'layout-detail': 'M4 5h16M4 12h16M4 19h16',
  'layout-compact': 'M4 6h16M4 12h16M4 18h16M8 4v16M16 4v16',
  sort: 'M7 4v16M4 7l3-3 3 3M17 20V4M14 17l3 3 3-3',
  download: 'M12 3v12M7 10l5 5 5-5M5 21h14',
  'rotate-ccw': 'M4 10a8 8 0 1 1 2 7M4 10V4M4 10h6',
  plus: 'M12 5v14M5 12h14',
  copy: 'M8 8h11v12H8zM5 16H4V4h12v1',
  trash: 'M5 7h14M10 11v6M14 11v6M8 7l1-3h6l1 3M7 7l1 14h8l1-14',
  save: 'M5 4h12l2 2v14H5zM8 4v6h8V4M9 20v-6h6v6',
  play: 'm9 6 9 6-9 6z',
  pencil: 'm14 5 5 5M4 20l4.5-1 10-10a2.1 2.1 0 0 0-3-3l-10 10L4 20Z',
  'more-vertical': 'M12 5v.01M12 12v.01M12 19v.01',
  filter: 'M4 5h16l-6 7v5l-4 2v-7L4 5Z',
  check: 'm5 12 4 4L19 6',
  move: 'M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20',
  crown: 'm3 5 4 4 5-6 5 6 4-4-2 14H5L3 5Z',
}

const props = withDefaults(defineProps<AppIconProps>(), {
  size: 16,
  strokeWidth: 1.8,
})

const path = computed<string>(() => ICON_PATHS[props.name])

const autoId = useId()
const titleId = computed<string>(() => `icon-${props.name}-${autoId}`)
</script>
